import * as React from "react";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import theme from "../../../theme";
import { Box, Grid } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { fetchVehicleDetail } from "../../../services/tecdocService";
import { VehicleDetail } from "../../../interfaces/VehicleDetail";
import {
  loadVinalfaVehicleContext,
  mapVinalfaContextToBasicRows,
  VinalfaVehicleContext,
} from "../../../interfaces/Vinalfa";

type BasicRow = { title: string; description: string };

const VehicleDetailComponent = () => {
  const [vehicleDetail, setVehicleDetail] =
    React.useState<VehicleDetail | null>(null);
  const [vinalfaCtx, setVinalfaCtx] =
    React.useState<VinalfaVehicleContext | null>(null);
  const [linkageTargetId, setLinkageTargetId] = React.useState(0);
  const [linkageTargetType, setLinkageTargetType] = React.useState("");
  const [tecdocFailed, setTecdocFailed] = React.useState(false);

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const typeParam = urlParams.get("linkageTargetType");
    const idParam = urlParams.get("linkageTargetId");
    const hashParam = urlParams.get("vehicleHash");

    if (idParam) {
      setLinkageTargetId(parseInt(idParam, 10));
    }
    if (typeParam) {
      setLinkageTargetType(typeParam);
    }

    const ctx = loadVinalfaVehicleContext();
    if (ctx && (!hashParam || ctx.vehicleHash === hashParam)) {
      setVinalfaCtx(ctx);
    } else if (ctx && hashParam && ctx.vehicleHash !== hashParam) {
      setVinalfaCtx(null);
    } else {
      setVinalfaCtx(ctx);
    }
  }, []);

  React.useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        setTecdocFailed(false);
        const response = await fetchVehicleDetail(
          linkageTargetType,
          linkageTargetId
        );
        if (!isMounted) return;
        if (response && response.mfrName) {
          setVehicleDetail(response);
        } else {
          setVehicleDetail(null);
          setTecdocFailed(true);
        }
      } catch {
        if (isMounted) {
          setVehicleDetail(null);
          setTecdocFailed(true);
        }
      }
    };

    if (linkageTargetId && linkageTargetType) {
      fetchData();
    } else {
      setTecdocFailed(true);
    }

    return () => {
      isMounted = false;
    };
  }, [linkageTargetId, linkageTargetType]);

  const useVinalfaFallback = !vehicleDetail && (!!vinalfaCtx || tecdocFailed);

  const headerTitle = vehicleDetail
    ? `${vehicleDetail.mfrName || ""} ${vehicleDetail.vehicleModelSeriesName || ""}`.trim()
    : vinalfaCtx
      ? `${vinalfaCtx.catalog || ""} ${vinalfaCtx.model || ""}`.trim()
      : "";

  const headerSubtitle = vehicleDetail
    ? vehicleDetail.description || ""
    : vinalfaCtx?.transmission || "";

  const imageUrl = vehicleDetail?.vehicleImages?.[0]?.imageURL800 || "";

  const rows: BasicRow[] = React.useMemo(() => {
    if (vehicleDetail) {
      const engineCodes =
        vehicleDetail.engines?.map((e) => e.code).filter(Boolean).join(" ") ||
        "";
      return [
        { title: "Make", description: vehicleDetail.mfrName || "" },
        {
          title: "Model",
          description: vehicleDetail.vehicleModelSeriesName || "",
        },
        { title: "Description", description: vehicleDetail.description || "" },
        {
          title: "Productions years",
          description: `${vehicleDetail.beginYearMonth || ""} - ${vehicleDetail.endYearMonth || ""}`,
        },
        {
          title: "Engine power [hp]",
          description: vehicleDetail.horsePowerTo != null
            ? String(vehicleDetail.horsePowerTo)
            : "",
        },
        {
          title: "Engine power [kw]",
          description: vehicleDetail.kiloWattsTo != null
            ? String(vehicleDetail.kiloWattsTo)
            : "",
        },
        {
          title: "Cylinders",
          description: vehicleDetail.cylinders != null
            ? String(vehicleDetail.cylinders)
            : "",
        },
        {
          title: "Capacity",
          description: vehicleDetail.capacityCC != null
            ? String(vehicleDetail.capacityCC)
            : "",
        },
        { title: "Engine Code", description: engineCodes },
        { title: "Dve System", description: vehicleDetail.driveType || "" },
        { title: "Fuel Type", description: vehicleDetail.fuelType || "" },
        { title: "Body Type", description: vehicleDetail.bodyStyle || "" },
      ];
    }
    if (useVinalfaFallback && vinalfaCtx) {
      return mapVinalfaContextToBasicRows(vinalfaCtx);
    }
    return mapVinalfaContextToBasicRows({
      vehicleHash: "",
      catalog: "",
      model: "",
      engine: "",
      date: "-",
      equipments: [],
    });
  }, [vehicleDetail, useVinalfaFallback, vinalfaCtx]);

  return (
    <>
      <Box
        sx={{
          bgcolor: "#f1f1f1",
          p: 2,
          borderTop: `2px solid ${theme.palette.primary.dark}`,
        }}
      >
        <Stack spacing={2} sx={{ paddingTop: "3px", paddingLeft: "5px" }}>
          <Typography
            variant="body1"
            sx={{ fontFamily: "Inter", fontSize: "15px", textAlign: "left" }}
          >
            <span style={{ fontWeight: 500 }}>{headerTitle}</span>{" "}
            {headerSubtitle}
          </Typography>
          {useVinalfaFallback && vinalfaCtx && (
            <Typography
              variant="caption"
              sx={{
                fontFamily: "Inter",
                color: theme.palette.text.primary,
                display: "block",
              }}
            >
              OEM catalog data (TecDoc vehicle not matched)
            </Typography>
          )}
        </Stack>
      </Box>
      <Box sx={{ borderBottom: `2px solid #f1f1f1`, pb: 2 }}>
        <Grid container spacing={2}>
          {imageUrl ? (
            <Grid item xs={12}>
              <img
                style={{ maxHeight: "100px" }}
                src={imageUrl}
                alt=""
              />
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <Box sx={{ bgcolor: "#f1f1f1", p: 1, mt: 1 }}>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: "Inter",
                  fontSize: "15px",
                  textAlign: "left",
                  fontWeight: 500,
                }}
              >
                Basic Data
              </Typography>
            </Box>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
                  <Table sx={{ minWidth: 200 }} aria-label="vehicle basic data">
                    <TableBody>
                      {rows.map((row) => (
                        <TableRow
                          key={row.title}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                            padding: "2px",
                          }}
                        >
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{ padding: "5px !important" }}
                          >
                            <span
                              style={{
                                fontSize: "13px",
                                fontFamily: "Inter",
                                fontWeight: 500,
                              }}
                            >
                              {row.title}
                            </span>
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{ padding: "5px !important" }}
                          >
                            <span
                              style={{
                                fontSize: "13px",
                                fontFamily: "Inter",
                                fontWeight: 400,
                              }}
                            >
                              {row.description}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default VehicleDetailComponent;
